import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Typography, Button, Paper, CircularProgress, Alert } from '@mui/material';
import { PhoneInput, validatePhoneNumber } from '../../components/PhoneInput';
import { getMyCustomerProfile, updateMyCustomerProfile } from '../../api/customerProfile';

export const ProfileComplete: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const { data: profile, isLoading } = useQuery({
    queryKey: ['my-customer-profile'],
    queryFn: getMyCustomerProfile,
  });

  const mutation = useMutation({
    mutationFn: () => updateMyCustomerProfile({ phoneNumber }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-customer-profile'] });
      const redirectTo = (location.state as { from?: string })?.from ?? '/buscar';
      navigate(redirectTo, { replace: true });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const error = validatePhoneNumber(phoneNumber);
    if (error) { setPhoneError(error); return; }
    setPhoneError('');
    mutation.mutate();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: 'text.headline' }} />
      </Box>
    );
  }

  // Already complete? Bounce
  if (profile?.isProfileComplete) {
    navigate('/buscar', { replace: true });
    return null;
  }

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', mt: { xs: 4, sm: 8 }, px: 3 }}>
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: { xs: 3, sm: 4 } }}>
        <Typography variant="h3" sx={{ color: 'text.headline', mb: 1 }}>
          Completa tu perfil
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Necesitamos tu número de teléfono para que la farmacia pueda contactarte sobre tu pedido.
        </Typography>

        {mutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(mutation.error as any)?.response?.data?.message ?? 'Error al guardar. Intenta de nuevo.'}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <PhoneInput
            label="Teléfono"
            value={phoneNumber}
            onChange={setPhoneNumber}
            error={!!phoneError}
            helperText={phoneError}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={mutation.isPending || !phoneNumber}
            sx={{ mt: 3, py: 1.5 }}
          >
            {mutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Continuar'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};
